/*******************************************************
* AccountReqForm
* 
* A form component for handling account requests. Manages all form state,
* validation, and submission logic internally.
* 
* Key Features:
* - Handles all form input and validation
* - Manages API communication for disciplines and form submission
* - Provides success/error feedback
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Alert,
} from '@mui/material';
import { FormTextField } from '@/src/components/common/FormTextField';
import { FormAutocomplete } from '@/src/components/common/FormAutocomplete';
import useLogin from '@/src/app/(pages)/Login/hooks/UseLogin';
import { commonEmailDomains } from '@/src/scripts/libs/clientUtilLib';
import {
    ACCOUNT_REQUEST_ROLES,
    buildAccountRequestPayload,
    createEmptyAccountRequestFormData,
    type AccountRequestCraft,
    type AccountRequestFormData,
    type AccountRequestRole,
    type AccountRequestShift,
} from './accountRequestOptions';
import ButtonRow from '@/src/components/common/ButtonRow';
import { tokens } from '@/src/theme/tokens';


// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface AccountReqFormProps {
    handleSuccess: (message: string) => void;
    handleCancel: () => void;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ACCOUNT_REQUEST_OPTIONS_ERROR = 'Failed to load account request options. Please try again later.';

const initialFormData = createEmptyAccountRequestFormData();
const roleOptions = [...ACCOUNT_REQUEST_ROLES];

//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
const renderErrorMessage = (message: string) => {
    return message.split('\n').map((line, index, array) => (
        <React.Fragment key={index}>
            {line}
            {index < array.length - 1 && <br />}
        </React.Fragment>
    ));
};


//#endregion


export default function AccountReqForm({ handleSuccess, handleCancel }: AccountReqFormProps) {
    //########## HOOKS ##########
    // #region HOOKS
    const { readAccountRequestOptions, requestAccount } = useLogin();

    const [formData, setFormData] = useState<AccountRequestFormData>(initialFormData);
    const [crafts, setCrafts] = useState<AccountRequestCraft[]>([]);
    const [shifts, setShifts] = useState<AccountRequestShift[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const loadOptions = async () => {
            try {
                const { data } = await readAccountRequestOptions();
                const crafts = data?.crafts || [];
                const shifts = data?.shifts || [];

                setCrafts(crafts);
                setShifts(shifts);

                if (crafts.length === 0 || shifts.length === 0) {
                    setError(ACCOUNT_REQUEST_OPTIONS_ERROR);
                }
            } catch (err) {
                console.error('Error fetching account request options:', err);
                setError(ACCOUNT_REQUEST_OPTIONS_ERROR);
            }
        };
        void loadOptions();
    }, [readAccountRequestOptions]);
    //#endregion

    //########## Main Logic ##########
    // #region Main Logic
    const handleValidate = useCallback((formData: AccountRequestFormData) => {
        const errors: Record<string, string> = {};

        if (!formData.EmployeeId) errors.EmployeeId = 'Employee ID is required';
        if (!formData.Email) errors.Email = 'Email is required';
        if (!formData.FirstName) errors.FirstName = 'First Name is required';
        if (!formData.LastName) errors.LastName = 'Last Name is required';
        if (!formData.Password) errors.Password = 'Password is required';
        if (!formData.Craft) errors.Craft = 'Craft is required';
        if (!formData.Shift) errors.Shift = 'Shift is required';
        if (!formData.Role) errors.Role = 'Role is required';
        if (!formData.DefaultJobID) errors.DefaultJobID = 'Job Number is required';

        if (formData.EmployeeId.length > 50) errors.EmployeeId = 'Employee ID must be 50 characters or less';
        if (formData.UserName && formData.UserName.length > 50) errors.UserName = 'Username must be 50 characters or less';
        if (formData.FirstName.length > 50) errors.FirstName = 'First Name must be 50 characters or less';
        if (formData.MiddleName && formData.MiddleName.length > 50) errors.MiddleName = 'Middle Name must be 50 characters or less';
        if (formData.LastName.length > 50) errors.LastName = 'Last Name must be 50 characters or less';
        if (formData.Email.length > 250) errors.Email = 'Email must be 250 characters or less';
        if (formData.Supervisor.length > 250) errors.Supervisor = 'Supervisor must be 250 characters or less';

        if (formData.Email && !EMAIL_REGEX.test(formData.Email)) {
            errors.Email = 'Please enter a valid email address';
        }

        return errors;
    }, []);

    const handleSubmit = useCallback(async () => {
        setError(null);
        setFieldErrors({});
        const errors = handleValidate(formData);
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await requestAccount(buildAccountRequestPayload(formData));
            if (result.status === 201) {
                handleSuccess('Account request submitted successfully. You will receive an email when your account is approved.');
                return;
            }

            if (result.status === 400) {
                setError(result.message);
                return;
            }

            if (result.status === 409) {
                if (result.message.includes('Job Number') && result.message.includes('invalid')) {
                    setFieldErrors(prev => ({
                        ...prev,
                        DefaultJobID: result.message
                    }));
                } else {
                    setError(result.message);
                }
                return;
            }

            setError(result.message || 'An error occurred while submitting your request.');
        } catch (error) {
            console.error('Error submitting account request:', error);
            setError('An error occurred while submitting your request. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    }, [handleValidate, formData, requestAccount, handleSuccess]);

    const emailSuggestions = useMemo(() => {
        const value = formData.Email;
        if (!value.includes('@')) return [];

        const [localPart, domainPart] = value.split('@');
        return commonEmailDomains
            .filter(d => !domainPart || d.toLowerCase().startsWith(domainPart.toLowerCase()))
            .map(d => `${localPart}@${d}`);
    }, [formData.Email]);

    const handleChange = useCallback((field: string, value: string | AccountRequestCraft | AccountRequestShift | AccountRequestRole | null) => {
        if (isSubmitting) return;

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }, [fieldErrors, isSubmitting]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        // Preserve the existing Enter-to-submit behavior for this form.
        if (event.key === 'Enter') {
            event.preventDefault();
            void handleSubmit();
        }
    }, [handleSubmit]);
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <Box
            component="form"
            noValidate
            sx={{ mt: 1, width: '100%' }}
            onKeyDown={handleKeyDown}
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {renderErrorMessage(error)}
                </Alert>
            )}

            <FormTextField
                name="EmployeeId"
                label="Employee ID"
                inputMode="numeric"
                required
                value={formData.EmployeeId}
                onChange={(field, value) => handleChange(field, (value).replace(/\D/g, ''))}
                error={fieldErrors.EmployeeId}
                autoComplete="off"
                disabled={isSubmitting}
            />

            <Autocomplete
                freeSolo
                fullWidth
                disableClearable
                options={emailSuggestions}
                inputValue={formData.Email}
                onInputChange={(_, newValue) => handleChange('Email', newValue ?? '')}
                sx={{
                    '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
                        padding: 0,
                    },
                    '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                        padding: '16.5px 14px', // rails-ignore: matches MUI's own outlined input so these fields line up
                    },
                }}
                renderInput={(params) => (
                    <FormTextField
                        {...params}
                        name="Email"
                        label="Email"
                        required
                        error={fieldErrors.Email}
                        autoComplete="email"
                        disabled={isSubmitting}
                    />
                )}
            />

            <FormTextField
                name="UserName"
                label="Username - (Optional)"
                value={formData.UserName}
                onChange={handleChange}
                error={fieldErrors.UserName}
                autoComplete="username"
                disabled={isSubmitting}
            />

            <FormTextField
                name="FirstName"
                label="First Name"
                required
                value={formData.FirstName}
                onChange={handleChange}
                error={fieldErrors.FirstName}
                autoComplete="given-name"
                disabled={isSubmitting}
            />

            <FormTextField
                name="MiddleName"
                label="Middle Name"
                value={formData.MiddleName}
                onChange={handleChange}
                error={fieldErrors.MiddleName}
                autoComplete="additional-name"
                disabled={isSubmitting}
            />

            <FormTextField
                name="LastName"
                label="Last Name"
                required
                value={formData.LastName}
                onChange={handleChange}
                error={fieldErrors.LastName}
                autoComplete="family-name"
                disabled={isSubmitting}
            />

            <FormTextField
                name="NickName"
                label="Nickname"
                value={formData.NickName}
                onChange={handleChange}
                error={fieldErrors.NickName}
                autoComplete="nickname"
                disabled={isSubmitting}
            />

            <FormTextField
                name="Supervisor"
                label="Supervisor"
                value={formData.Supervisor}
                onChange={handleChange}
                error={fieldErrors.Supervisor}
                autoComplete="off"
                disabled={isSubmitting}
            />

            <FormAutocomplete
                sortOptions={false}
                name="Craft"
                label="Craft"
                options={crafts}
                value={formData.Craft}
                disabled={isSubmitting}
                disableClearable={formData.Craft !== null}
                required
                error={fieldErrors.Craft}
                onChange={handleChange}
                useMobileSelect={false}
            />

            <FormAutocomplete
                sortOptions={false}
                name="Shift"
                label="Shift"
                options={shifts}
                value={formData.Shift}
                disabled={isSubmitting}
                disableClearable={formData.Shift !== null}
                required
                error={fieldErrors.Shift}
                onChange={handleChange}
                useMobileSelect={false}
            />

            {/* Grouped field, managers, office, so not sorted A-Z */}
            <FormAutocomplete
                sortOptions={false}
                name="Role"
                label="Role"
                options={roleOptions}
                value={formData.Role}
                disabled={isSubmitting}
                disableClearable={formData.Role !== null}
                required
                error={fieldErrors.Role}
                onChange={handleChange}
                useMobileSelect={false}
            />

            <FormTextField
                inputMode='numeric'
                name="DefaultJobID"
                label="Job Number"
                type="number"
                onWheel={(event) => (event.target as HTMLInputElement)}
                required
                value={formData.DefaultJobID}
                onChange={handleChange}
                error={fieldErrors.DefaultJobID}
                disabled={isSubmitting}
            />

            <FormTextField
                name="Password"
                label="Password"
                type="password"
                required
                value={formData.Password}
                onChange={handleChange}
                error={fieldErrors.Password}
                autoComplete="new-password"
                disabled={isSubmitting}
            />

            <ButtonRow align="between" sx={{ mt: tokens.space.section }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{ width: '45%' }}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => void handleSubmit()}
                    loading={isSubmitting}
                    loadingPosition="end"
                    variant="contained"
                    sx={{ width: '45%' }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting' : 'Submit Request'}
                </Button>
            </ButtonRow>
        </Box>
    );
    //#endregion
}
